import { SceneLoader } from "@babylonjs/core/Loading/sceneLoader";
import { MultiMaterial } from "@babylonjs/core/Materials/multiMaterial";
import { Geometry } from "@babylonjs/core/Meshes/geometry";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { SubMesh } from "@babylonjs/core/Meshes/subMesh";
import { Tools } from "@babylonjs/core/Misc/tools";
import { MorphTarget } from "@babylonjs/core/Morph/morphTarget";
import { MorphTargetManager } from "@babylonjs/core/Morph/morphTargetManager";
import { MmdModelLoader } from "../mmdModelLoader";
import { ObjectUniqueIdProvider } from "../objectUniqueIdProvider";
import { PmxObject } from "../Parser/pmxObject";
import { SdefBufferKind } from "../sdefBufferKind";
import { BpmxReader } from "./Parser/bpmxReader";
/**
 * BpmxLoader is a loader that loads models in BPMX format
 *
 * BPMX is a single binary file format that contains all the data of a model
 */
export class BpmxLoader extends MmdModelLoader {
    /**
     * Create a new BpmxLoader
     */
    constructor() {
        super("bpmx", {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ".bpmx": { isBinary: true }
        });
    }
    loadFile(scene, fileOrUrl, _rootUrl, onSuccess, onProgress, useArrayBuffer, onError) {
        const materialBuilder = this.materialBuilder;
        const useSdef = this.useSdef;
        const buildSkeleton = this.buildSkeleton;
        const buildMorph = this.buildMorph;
        const boundingBoxMargin = this.boundingBoxMargin;
        const request = scene._loadFile(fileOrUrl, (data, responseURL) => {
            const loadState = {
                arrayBuffer: data,
                pmFileId: fileOrUrl instanceof File ? ObjectUniqueIdProvider.GetId(fileOrUrl).toString() : fileOrUrl,
                materialBuilder,
                useSdef,
                buildSkeleton,
                buildMorph,
                boundingBoxMargin
            };
            onSuccess(loadState, responseURL);
        }, onProgress, true, useArrayBuffer, onError);
        return request;
    }
    async _parseFileAsync(arrayBuffer) {
        return await BpmxReader.ParseAsync(arrayBuffer, this)
            .catch((e) => {
            return Promise.reject(e);
        });
    }
    _getProgressTaskCosts(state, modelObject) {
        const tasks = super._getProgressTaskCosts(state, modelObject);
        tasks.push({ name: "Build Geometry", cost: modelObject.geometry.indices.length });
        return tasks;
    }
    async _buildGeometryAsync(state, modelObject, mesh, scene, assetContainer, progress) {
        const vertexData = new VertexData();
        vertexData.positions = modelObject.geometry.positions;
        vertexData.normals = modelObject.geometry.normals;
        vertexData.uvs = modelObject.geometry.uvs;
        vertexData.indices = modelObject.geometry.indices;
        vertexData.matricesIndices = modelObject.geometry.matricesIndices;
        vertexData.matricesWeights = modelObject.geometry.matricesWeights;
        scene._blockEntityCollection = !!assetContainer;
        const geometry = new Geometry(modelObject.header.modelName, scene, vertexData, false);
        geometry._parentContainer = assetContainer;
        scene._blockEntityCollection = false;
        if (state.useSdef && modelObject.geometry.sdef !== undefined) {
            const sdefData = modelObject.geometry.sdef;
            const vetexCount = vertexData.positions.length / 3;
            for (let i = 0; i < vetexCount; ++i) {
                const boneWeight0 = vertexData.matricesWeights[i * 4 + 0];
                const boneWeight1 = vertexData.matricesWeights[i * 4 + 1];
                const sdefC = sdefData.c;
                const sdefR0 = sdefData.r0;
                const sdefR1 = sdefData.r1;
                const centerX = sdefC[i * 3 + 0];
                const centerY = sdefC[i * 3 + 1];
                const centerZ = sdefC[i * 3 + 2];
                // calculate rw0 and rw1
                let r0X = sdefR0[i * 3 + 0];
                let r0Y = sdefR0[i * 3 + 1];
                let r0Z = sdefR0[i * 3 + 2];
                let r1X = sdefR1[i * 3 + 0];
                let r1Y = sdefR1[i * 3 + 1];
                let r1Z = sdefR1[i * 3 + 2];
                const rwX = r0X * boneWeight0 + r1X * boneWeight1;
                const rwY = r0Y * boneWeight0 + r1Y * boneWeight1;
                const rwZ = r0Z * boneWeight0 + r1Z * boneWeight1;
                r0X = centerX + r0X - rwX;
                r0Y = centerY + r0Y - rwY;
                r0Z = centerZ + r0Z - rwZ;
                r1X = centerX + r1X - rwX;
                r1Y = centerY + r1Y - rwY;
                r1Z = centerZ + r1Z - rwZ;
                const cr0X = (centerX + r0X) * 0.5;
                const cr0Y = (centerY + r0Y) * 0.5;
                const cr0Z = (centerZ + r0Z) * 0.5;
                const cr1X = (centerX + r1X) * 0.5;
                const cr1Y = (centerY + r1Y) * 0.5;
                const cr1Z = (centerZ + r1Z) * 0.5;
                sdefR0[i * 3 + 0] = cr0X;
                sdefR0[i * 3 + 1] = cr0Y;
                sdefR0[i * 3 + 2] = cr0Z;
                sdefR1[i * 3 + 0] = cr1X;
                sdefR1[i * 3 + 1] = cr1Y;
                sdefR1[i * 3 + 2] = cr1Z;
            }
            geometry.setVerticesData(SdefBufferKind.MatricesSdefCKind, sdefData.c, false, 3);
            geometry.setVerticesData(SdefBufferKind.MatricesSdefR0Kind, sdefData.r0, false, 3);
            geometry.setVerticesData(SdefBufferKind.MatricesSdefR1Kind, sdefData.r1, false, 3);
        }
        geometry.applyToMesh(mesh);
        progress.endTask("Build Geometry");
        progress.invokeProgressEvent();
        return { vertexData, geometry };
    }
    async _buildMaterialAsync(state, modelObject, mesh, scene, assetContainer, vertexData, rootUrl, progress) {
        scene._blockEntityCollection = !!assetContainer;
        const multiMaterial = new MultiMaterial(modelObject.header.modelName + "_multi", scene);
        multiMaterial._parentContainer = assetContainer;
        scene._blockEntityCollection = false;
        let buildMaterialsPromise = undefined;
        const texturePathTable = new Array(modelObject.textures.length);
        for (let i = 0; i < modelObject.textures.length; ++i) {
            texturePathTable[i] = modelObject.textures[i].relativePath;
        }
        const textureLoadPromise = new Promise((resolve) => {
            buildMaterialsPromise = state.materialBuilder.buildMaterials(mesh.uniqueId, // uniqueId
            modelObject.materials, // materialsInfo
            texturePathTable, // texturePathTable
            rootUrl, // rootUrl
            "file:" + state.pmFileId + "_", // fileRootId
            modelObject.textures, // referenceFiles
            scene, // scene
            assetContainer, // assetContainer
            vertexData.indices, // indices
            vertexData.uvs, // uvs
            multiMaterial, // multiMaterial
            this, // logger
            (event) => {
                if (!event.lengthComputable)
                    return;
                progress.setTaskProgressRatio("Texture Load", event.loaded / event.total, true);
                progress.invokeProgressEvent();
            }, // onTextureLoadProgress
            () => resolve() // onTextureLoadComplete
            );
        });
        if (buildMaterialsPromise !== undefined) {
            await buildMaterialsPromise;
        }
        mesh.material = multiMaterial;
        progress.endTask("Build Material");
        progress.invokeProgressEvent();
        return { multiMaterial, textureLoadPromise };
    }
    _buildSubMeshes(modelObject, mesh) {
        if (mesh.subMeshes === undefined)
            return;
        mesh.subMeshes.length = 0;
        const materials = modelObject.materials;
        let offset = 0;
        for (let i = 0; i < materials.length; ++i) {
            const materialInfo = materials[i];
            new SubMesh(i, // materialIndex
            0, // verticesStart
            Math.floor(modelObject.geometry.positions.length / 3), // verticesCount
            offset, // indexStart
            materialInfo.surfaceCount, // indexCount
            mesh);
            offset += materialInfo.surfaceCount;
        }
    }
    async _buildMorphAsync(modelObject, mesh, scene, assetContainer, vertexData, morphsMetadata, progress) {
        scene._blockEntityCollection = !!assetContainer;
        const morphTargetManager = new MorphTargetManager(scene);
        morphTargetManager._parentContainer = assetContainer;
        scene._blockEntityCollection = false;
        {
            const morphsInfo = modelObject.morphs;
            const morphTargets = [];
            let buildMorphProgress = 0;
            for (let i = 0; i < morphsInfo.length; ++i) {
                const morphInfo = morphsInfo[i];
                // create morph metadata
                switch (morphInfo.type) {
                    case PmxObject.Morph.Type.GroupMorph:
                    case PmxObject.Morph.Type.BoneMorph:
                    case PmxObject.Morph.Type.MaterialMorph:
                        morphsMetadata.push(morphInfo);
                        break;
                    case PmxObject.Morph.Type.VertexMorph:
                    case PmxObject.Morph.Type.UvMorph:
                    case PmxObject.Morph.Type.AdditionalUvMorph1:
                    case PmxObject.Morph.Type.AdditionalUvMorph2:
                    case PmxObject.Morph.Type.AdditionalUvMorph3:
                    case PmxObject.Morph.Type.AdditionalUvMorph4:
                        morphsMetadata.push({
                            name: morphInfo.name,
                            englishName: morphInfo.englishName,
                            category: morphInfo.category,
                            type: morphInfo.type,
                            index: morphTargets.length
                        });
                        break;
                }
                if (morphInfo.type !== PmxObject.Morph.Type.VertexMorph &&
                    morphInfo.type !== PmxObject.Morph.Type.UvMorph &&
                    morphInfo.type !== PmxObject.Morph.Type.AdditionalUvMorph1 &&
                    morphInfo.type !== PmxObject.Morph.Type.AdditionalUvMorph2 &&
                    morphInfo.type !== PmxObject.Morph.Type.AdditionalUvMorph3 &&
                    morphInfo.type !== PmxObject.Morph.Type.AdditionalUvMorph4) {
                    // group morph, bone morph, material morph will be handled by cpu bound custom runtime
                    continue;
                }
                const morphTarget = new MorphTarget(morphInfo.name, 0, scene);
                morphTargets.push(morphTarget);
                if (morphInfo.type === PmxObject.Morph.Type.VertexMorph) {
                    const positions = new Float32Array(modelObject.geometry.positions);
                    positions.set(vertexData.positions);
                    const morphIndices = morphInfo.indices;
                    const positionOffsets = morphInfo.positions;
                    let time = performance.now();
                    for (let j = 0; j < morphIndices.length; ++j) {
                        const elementIndex = morphIndices[j];
                        positions[elementIndex * 3 + 0] += positionOffsets[j * 3 + 0];
                        positions[elementIndex * 3 + 1] += positionOffsets[j * 3 + 1];
                        positions[elementIndex * 3 + 2] += positionOffsets[j * 3 + 2];
                        if (j % 10000 === 0 && 100 < performance.now() - time) {
                            progress.setTaskProgress("Build Morph", buildMorphProgress + j);
                            progress.invokeProgressEvent();
                            await Tools.DelayAsync(0);
                            time = performance.now();
                        }
                    }
                    progress.setTaskProgress("Build Morph", buildMorphProgress + morphIndices.length);
                    buildMorphProgress += morphIndices.length;
                    morphTarget.setPositions(positions);
                }
                else /*if (morphInfo.type === PmxObject.Morph.Type.uvMorph)*/ {
                    const uvs = new Float32Array(modelObject.geometry.uvs);
                    uvs.set(vertexData.uvs);
                    const morphIndices = morphInfo.indices;
                    const uvOffsets = morphInfo.offsets;
                    let time = performance.now();
                    for (let j = 0; j < morphIndices.length; ++j) {
                        const elementIndex = morphIndices[j];
                        uvs[elementIndex * 2 + 0] += uvOffsets[j * 4 + 0];
                        uvs[elementIndex * 2 + 1] += uvOffsets[j * 4 + 1];
                        if (j % 10000 === 0 && 100 < performance.now() - time) {
                            progress.setTaskProgress("Build Morph", buildMorphProgress + j);
                            progress.invokeProgressEvent();
                            await Tools.DelayAsync(0);
                            time = performance.now();
                        }
                    }
                    progress.setTaskProgress("Build Morph", buildMorphProgress + morphIndices.length);
                    buildMorphProgress += morphIndices.length;
                    morphTarget.setPositions(vertexData.positions);
                    morphTarget.setUVs(uvs);
                }
            }
            morphTargetManager.areUpdatesFrozen = true;
            for (let i = 0; i < morphTargets.length; ++i) {
                morphTargetManager.addTarget(morphTargets[i]);
            }
            morphTargetManager.areUpdatesFrozen = false;
            progress.endTask("Build Morph");
        }
        return mesh.morphTargetManager = morphTargetManager;
    }
}
if (SceneLoader) {
    SceneLoader.RegisterPlugin(new BpmxLoader());
}
